import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * BluWave-shaped button. Defaults to a navy pill that inverts on hover
 * (signature interaction: navy fill ⇄ navy outline + navy text on
 * transparent). Sizes keep their compact heights but the primitive's
 * radius is always pill — sharp corners aren't part of the system.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill font-sans text-sm font-semibold leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bw-navy focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[var(--bw-disabled-bg)] disabled:text-[var(--bw-disabled-text)] disabled:pointer-events-none",
  {
    variants: {
      variant: {
        // Navy pill, inverts on hover.
        default:
          "bg-bw-navy text-white border border-transparent hover:bg-transparent hover:text-bw-navy hover:border-bw-navy",
        // White pill with navy text — used as a quieter secondary CTA.
        secondary:
          "bg-white text-bw-navy border border-bw-navy hover:bg-bw-navy hover:text-white",
        // Bordered, transparent fill — for low-emphasis actions like
        // Load-more / sort-toggle / Copy-for-LLM.
        outline:
          "bg-transparent text-bw-navy border border-bw-navy/40 hover:border-bw-navy hover:bg-bw-navy/5",
        // No chrome until interaction; used inside dense rows (e.g. table
        // sort headers) where a full pill would be visually noisy.
        ghost:
          "bg-transparent text-bw-navy hover:bg-bw-navy/5",
        // Inline link styling (text only, no pill chrome).
        link:
          "rounded-none bg-transparent text-bw-blue underline-offset-4 hover:text-bw-blue-hover hover:underline",
        // Brand-danger destructive (rescind / delete actions).
        destructive:
          "bg-bw-danger text-white border border-transparent hover:bg-transparent hover:text-bw-danger hover:border-bw-danger",
      },
      size: {
        default: "h-10 px-6 py-2",
        sm: "h-8 px-4 text-xs",
        lg: "h-12 px-8 text-sm",
        // Icon buttons stay circular per brand (50% radius).
        icon: "h-9 w-9 rounded-full p-0",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
